import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Search, Users, Instagram, Youtube, ChevronDown, ArrowUpDown, User,
} from 'lucide-react';
import { PageHeader, SectionCard, EmptyState, DataTableSkeleton } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_CREATORS } from '../demo/data';

// ── Types ──────────────────────────────────────────────────────────────────

interface Creator {
  id: number;
  influencer_id: string;
  slug: string;
  stage_name: string;
  platform: string;
  follower_count: number;
  engagement_rate: number;
  profile_image_url: string | null;
  orders: number;
  revenue: number;
  category: string;
}

type SortKey = 'revenue' | 'orders' | 'followers';

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtFollowers = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${Math.round(n / 1_000)}K`
    : String(n);

const fmtCurrency = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
    : `$${n.toLocaleString()}`;

const PLATFORM_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  instagram: { bg: '#E1306C', text: '#fff', label: 'Instagram' },
  tiktok:    { bg: '#000000', text: '#fff', label: 'TikTok' },
  youtube:   { bg: '#FF0000', text: '#fff', label: 'YouTube' },
};

function PlatformBadge({ platform }: { platform: string }) {
  const style = PLATFORM_STYLES[platform?.toLowerCase()] ?? { bg: 'rgba(255,255,255,0.1)', text: '#fff', label: platform };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: style.bg, color: style.text, borderRadius: '2px' }}
    >
      <PlatformIcon platform={platform} />
      {style.label}
    </span>
  );
}

function PlatformIcon({ platform }: { platform: string }) {
  const p = platform?.toLowerCase();
  if (p === 'instagram') return <Instagram className="w-2.5 h-2.5" />;
  if (p === 'youtube') return <Youtube className="w-2.5 h-2.5" />;
  if (p === 'tiktok') return (
    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.3 6.3 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.76a8.21 8.21 0 0 0 4.79 1.52V6.84a4.85 4.85 0 0 1-1.02-.15z" />
    </svg>
  );
  return <User className="w-2.5 h-2.5" />;
}

function AvatarWithFallback({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div className="w-14 h-14 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/30 shrink-0">
        <User className="w-6 h-6" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="w-14 h-14 rounded-full object-cover border border-white/[0.08] shrink-0"
      style={{ background: 'rgba(255,255,255,0.04)' }}
    />
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export default function VendorCreators() {
  const { t } = useTranslation(['creators', 'common']);
  const { isDemoMode } = useDemo();

  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchRaw, setSearchRaw] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [platform, setPlatform] = useState('all');
  const [sortBy, setSortBy] = useState<SortKey>('revenue');

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = useCallback((val: string) => {
    setSearchRaw(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchDebounced(val), 350);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    if (isDemoMode) {
      setCreators((DEMO_VENDOR_CREATORS as Creator[] | undefined) ?? []);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/vendor/influencers`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setCreators(json.data ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isDemoMode]);

  // ── Filtering & sorting ──
  const filtered = creators
    .filter(c => {
      if (platform !== 'all' && c.platform?.toLowerCase() !== platform) return false;
      if (searchDebounced) {
        const q = searchDebounced.toLowerCase();
        return (
          c.stage_name?.toLowerCase().includes(q) ||
          c.influencer_id?.toLowerCase().includes(q) ||
          c.slug?.toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'revenue') return b.revenue - a.revenue;
      if (sortBy === 'orders') return b.orders - a.orders;
      return b.follower_count - a.follower_count;
    });

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <PageHeader
        title={t('creators:title')}
        subtitle={t('creators:subtitle')}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <input
            type="search"
            value={searchRaw}
            onChange={e => handleSearch(e.target.value)}
            placeholder={t('creators:search')}
            className="input pl-9 py-2 text-sm w-full"
          />
        </div>

        {/* Platform filter */}
        <div className="relative">
          <select
            value={platform}
            onChange={e => setPlatform(e.target.value)}
            className="input py-2 text-sm pr-8 appearance-none cursor-pointer"
          >
            <option value="all">{t('creators:allPlatforms')}</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="youtube">YouTube</option>
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
        </div>

        {/* Sort */}
        <div className="relative">
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as SortKey)}
            className="input py-2 text-sm pr-8 appearance-none cursor-pointer"
          >
            <option value="revenue">{t('creators:sortRevenue')}</option>
            <option value="orders">{t('creators:sortOrders')}</option>
            <option value="followers">{t('creators:sortFollowers')}</option>
          </select>
          <ArrowUpDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <SectionCard><DataTableSkeleton rows={6} cols={3} /></SectionCard>
      ) : error ? (
        <SectionCard>
          <p className="text-red-400 text-sm py-8 text-center">{error}</p>
        </SectionCard>
      ) : filtered.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<Users className="w-6 h-6" />}
            title={t('common:noResults')}
            message={searchDebounced || platform !== 'all' ? t('common:noResults') : t('creators:empty')}
          />
        </SectionCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(creator => (
            <CreatorCard key={creator.id} creator={creator} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Creator Card ───────────────────────────────────────────────────────────

function CreatorCard({ creator, t }: { creator: Creator; t: (k: string) => string }) {
  const avatarUrl = creator.profile_image_url
    || `https://api.dicebear.com/8.x/avataaars/svg?seed=${encodeURIComponent(creator.influencer_id)}&backgroundColor=ffd400`;

  return (
    <div
      className="card p-5 flex flex-col gap-4 hover:border-white/[0.12] transition-colors"
    >
      {/* Header row */}
      <div className="flex items-center gap-3.5">
        <AvatarWithFallback src={avatarUrl} name={creator.stage_name} />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white truncate">{creator.stage_name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-white/40 truncate">@{creator.influencer_id}</span>
            <PlatformBadge platform={creator.platform} />
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatPill label={t('creators:followers')} value={`${fmtFollowers(creator.follower_count)} ${t('creators:followers').toLowerCase()}`} />
        <StatPill label={t('creators:engagement')} value={`${(creator.engagement_rate * 100).toFixed(1)}%`} />
        <StatPill label={t('creators:revenueAttributed')} value={fmtCurrency(creator.revenue)} accent />
        <StatPill label={t('creators:ordersAttributed')} value={`${creator.orders.toLocaleString()} ${t('creators:orders').toLowerCase()}`} />
      </div>

      {/* View Profile link */}
      <Link
        to={`/vendor/creators/${creator.slug}`}
        className="btn-ghost text-xs text-center py-2 mt-auto"
      >
        {t('creators:viewProfile')} →
      </Link>
    </div>
  );
}

function StatPill({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] px-3 py-2" style={{ borderRadius: '2px' }}>
      <p className="text-[10px] text-white/35 uppercase tracking-wider font-medium truncate">{label}</p>
      <p className={`text-sm font-semibold mt-0.5 truncate ${accent ? 'text-[#FFD400]' : 'text-white'}`}>{value}</p>
    </div>
  );
}
